import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { money, calcTotals, shortDate, isEquipmentPageJob } from "@/lib/invoice";
import { BLOCKS, PAGE_W, label } from "@/lib/invoiceLayout";

const RULE = "repeating-linear-gradient(to bottom, transparent 0, transparent calc(var(--lh) - 1px), #555 calc(var(--lh) - 1px), #555 var(--lh))";
let logoPromise = null;
export const resetLogoCache = () => { logoPromise = null; };
const loadLogo = () => (logoPromise ||= api.get("/branding/logo", { responseType: "blob" }).then((r) => URL.createObjectURL(r.data)).catch(() => null));

const U = ({ children, className = "", w, grow }) => (
  <span className={`inline-block border-b border-black px-1 align-baseline whitespace-nowrap overflow-hidden ${grow ? "flex-1" : ""} ${className}`} style={{ minWidth: w }}>{children || "\u00a0"}</span>
);
const Row = ({ children }) => <p className="flex items-end gap-1 whitespace-nowrap" style={{ lineHeight: "1.8em" }}>{children}</p>;
const Ruled = ({ text, lines, lh, size }) => (
  <div className="whitespace-pre-wrap" style={{ "--lh": `${lh}px`, lineHeight: `${lh}px`, fontSize: size, backgroundImage: RULE, minHeight: lines * lh }}>{text}</div>
);
const numbered = (arr) => (arr || []).filter((x) => String(x).trim()).map((x, i) => `${i + 1}) ${x}`).join("\n");

const LIST = { scales: "scales", other_equipment: "other_equipment", lifts: "lifts" };

function Body({ it, v, s, t, logo, edit, attach }) {
  const L = (k) => label(it, k);
  const c = v.customer || {};
  const z = it.size;
  switch (it.key) {
    case "company":
      return (
        <div className="text-center leading-tight">
          <p className="italic">{s.company_name}</p>
          <p style={{ fontSize: z * 0.67 }}>{s.address_line1}</p>
          <p style={{ fontSize: z * 0.67 }}>{s.address_line2}</p>
          <p style={{ fontSize: z * 0.5, marginTop: 4 }}>{s.email}</p>
        </div>
      );
    case "invoice_hdr":
      return (
        <div style={{ lineHeight: 1.4 }}>
          <p>{L("invoice")} <U w={z * 6.3}>{v.number || "—"}</U></p>
          <p>{L("phone")} &nbsp;{s.phone}</p>
          <p>{L("toll")} {s.toll_free}</p>
        </div>
      );
    case "customer":
      return (<div>
        <Row><U grow className="font-bold">{c.name}</U></Row>
        <Row><U w="73%">{c.address}</U></Row>
        <Row><U w="73%">{c.city_state_zip}</U></Row>
        <Row>{L("phone")} <U w="53%" className="font-bold text-center">{c.phone}</U></Row>
      </div>);
    case "service_info":
      return (<div>
        <Row>{L("tech")} <U w={z * 2.4}>{v.tech}</U> {L("date")} <U grow className="text-right">{shortDate(v.service_date)}</U></Row>
        <Row>{L("admin")} <U grow>{c.administrator}</U></Row>
        <Row>{L("contact")} <U grow>{c.contact}</U></Row>
        <Row>{L("corp")} <U grow className="font-bold">{c.corporate_info}</U></Row>
      </div>);
    case "months":
      return (
        <p className="flex items-end whitespace-nowrap" style={{ lineHeight: "1.6em" }}>
          {(v.months || []).map((m, i) => <span key={i} className="mr-1 inline-flex items-end">{i + 1}:<U w={z * 1.47} className="font-bold text-center">{m ? "x" : ""}</U></span>)}
          <span className="ml-1">{L("freq")}</span><U grow className="text-center">{v.frequency}</U>
        </p>
      );
    case "service_performed":
      return (<div>
        <p className="font-bold leading-tight" style={{ fontSize: z * 1.33 }}>{L("title")}</p>
        <Ruled text={v.service_performed} lines={it.lines} lh={z * 1.4} size={z * 0.97} />
      </div>);
    case "scales":
    case "other_equipment":
    case "lifts":
      return (<div>
        <div className="flex items-end gap-8 font-bold">
          <span>{L("title")}</span>
          {it.key === "scales" && <span className="flex-1 flex items-end gap-1">{L("sticker")} <span className="flex-1 border-b border-black text-center" style={{ fontSize: z * 1.27 }}>{attach ? "\u00a0" : (v.sticker_nos || "\u00a0")}</span></span>}
        </div>
        <Ruled text={attach ? (it.key === "scales" ? "SEE ATTACHED" : "") : numbered(v[LIST[it.key]])} lines={it.lines} lh={z * 1.53} size={z * 1.17} />
      </div>);
    case "signatures":
      return (<div className="pt-6 space-y-9">
        <p className="flex gap-2"><span className="border-b border-black flex-[2]">{L("tech")}</span><span className="border-b border-black flex-1">{L("date")}</span></p>
        <p className="flex gap-2"><span className="border-b border-black flex-[2]">{L("customer")}</span><span className="border-b border-black flex-1">{L("date")}</span></p>
      </div>);
    case "totals": {
      const big = { fontSize: z * 1.47 };
      return (
        <dl className="grid grid-cols-[1fr_60%] items-end gap-x-2 text-right">
          <dt>{L("subtotal")}</dt><dd className="border-b border-black" style={big}>{money(v.subtotal)}</dd>
          <dt>{L("tax")}</dt><dd className="border-b border-black" style={big}>{money(t.tax)}</dd>
          <dt className="font-bold mt-2">{L("total")}</dt><dd className="font-bold border-b border-black" style={big}>{money(t.total)}</dd>
          <dt className="font-bold">{L("parts")}</dt><dd className="border-b border-black" style={big}>{Number(v.parts) ? money(v.parts) : "\u00A0"}</dd>
          <dt className="font-bold" style={{ fontSize: z * 1.33 }}>{L("grand")}</dt><dd className="font-bold border-b border-black" style={big}>{v.plus_total !== null && v.plus_total !== undefined && v.plus_total !== "" ? money(t.grand_total) : "\u00A0"}</dd>
          <dt className="col-span-2 text-left pl-12 text-blue-700 font-bold" style={{ fontSize: z * 0.87 }}>{v.terms}</dt>
        </dl>
      );
    }
    case "text":
      return <p className="whitespace-pre-wrap leading-snug">{it.text}</p>;
    case "logo":
      return logo ? <img src={logo} alt="" draggable={false} style={{ width: "100%", height: it.h, objectFit: "contain" }} />
        : <div style={{ height: it.h }} className="border border-dashed border-neutral-400 text-neutral-400 text-xs flex items-center justify-center">Logo (upload on Stickers page)</div>;
    case "line":
      return <div style={{ borderTop: `${Math.max(1, it.h)}px solid #000` }} />;
    default:
      return null;
  }
}

export default function LayoutInvoice({ invoice: v, settings: s, layout, selected, onPointerDown, onResize, copyLabel }) {
  const [logo, setLogo] = useState(null);
  const [height, setHeight] = useState(1056);
  const page = useRef(null);
  const items = layout.items;
  useLayoutEffect(() => {
    const kids = [...page.current.children];
    setHeight(Math.max(1056, ...kids.map((k) => k.offsetTop + k.offsetHeight + 16)));
  }, [items, v, logo]);
  useEffect(() => { if (items.some((i) => i.key === "logo" && !i.hidden)) loadLogo().then(setLogo); }, [items]);
  const t = calcTotals(v);
  const edit = !!onPointerDown;
  // Only the dedicated-equipment-page facilities get the blank page-1 equipment section + SEE ATTACHED note.
  const attach = !edit && isEquipmentPageJob(v.customer?.name);
  return (
    <div ref={page} className="invoice-paper bg-white text-black shadow-xl mx-auto relative overflow-hidden" data-testid="invoice-document"
      style={{ width: PAGE_W, height, fontFamily: "'Times New Roman', Tinos, Times, serif" }}>
      {copyLabel && <p className="absolute top-1 right-5 font-bold text-[18px] z-10" data-testid="invoice-copy-label">{copyLabel}</p>}
      {items.map((it, k) => (it.hidden && !edit) || !BLOCKS[it.key] ? null : (
        <div key={it.id || it.key} data-idx={k} data-testid={`layout-item-${it.id || it.key}`} onPointerDown={edit ? (e) => onPointerDown(e, k) : undefined}
          style={{ position: "absolute", left: it.x, top: it.y, width: it.w, fontSize: it.size, fontWeight: it.bold ? 700 : 400, opacity: it.hidden ? 0.25 : 1,
            cursor: edit ? "move" : undefined, userSelect: edit ? "none" : undefined, padding: it.key === "line" ? "3px 0" : undefined,
            outline: edit ? (selected === k ? "2px solid #10b981" : "1px dashed rgba(16,185,129,.45)") : undefined, outlineOffset: 2 }}>
          <Body it={it} v={v} s={s} t={t} logo={logo} edit={edit} attach={attach} />
          {edit && selected === k && (
            <span onPointerDown={(e) => { e.stopPropagation(); onResize(e, k); }} data-testid="layout-resize-handle"
              style={{ position: "absolute", right: 0, top: "50%", width: 10, height: 24, marginTop: -12, background: "#10b981", borderRadius: 3, cursor: "ew-resize" }} />
          )}
        </div>
      ))}
    </div>
  );
}
